import { useEffect, useState } from "react";
import styled from "styled-components";
import { Button } from "@blueprintjs/core";
import { faCheckCircle, faKey } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Code } from "@components";
import { useOrg } from "@hooks";

import { createToken } from "@pages/settings/api";
import { TokenResponse } from "@pages/settings/types";
import { CIProviderStepOneTokenProps } from "@pages/setup/types";

import { ErrorText } from "../SetupStyledComponents";

import { StepItem } from "./StepItem";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InputLabel = styled.div`
  font-size: 13px;
  color: var(--gray1);
`;

const TokenCopyBlock: React.FC<{ tokenResponse?: TokenResponse }> = ({
  tokenResponse,
}) => {
  return (
    <Container>
      <div>
        <InputLabel>Name</InputLabel>
        <Code copyable light>
          SEMGREP_APP_TOKEN
        </Code>
      </div>
      <div>
        <InputLabel>Value</InputLabel>
        <Code className="fs-mask dd-privacy-mask" copyable light>
          {tokenResponse?.token ?? "<Error, please get a token via Settings>"}
        </Code>
      </div>
      <ErrorText>
        Save a copy of this token somewhere secure now; you will not be able to
        view it after leaving this page.
      </ErrorText>
    </Container>
  );
};

/**
 * Returns StepItem component that generates token
 */
export const StepOneToken: React.FC<CIProviderStepOneTokenProps> = (props) => {
  const org = useOrg();
  const [isTokenVisible, setIsTokenVisible] = useState(false);
  const [isLoadingToken, setIsLoadingToken] = useState(false);
  const [tokenResponse, setTokenResponse] = useState<TokenResponse>();

  useEffect(() => {
    if (!isTokenVisible) return;
    setIsLoadingToken(true);
    createToken(org!.id)
      .then(setTokenResponse)
      .then(() => setIsLoadingToken(false));
  }, [org, isTokenVisible]);

  return (
    <StepItem
      number={1}
      title="Add secret"
      description={
        <div>
          For authentication Semgrep uses API tokens which you should store as a{" "}
          <a
            href={props.secretDocsHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            {props.secretName}
          </a>
          .
        </div>
      }
      button={
        <Button
          onClick={() => setIsTokenVisible(!isTokenVisible)}
          loading={isLoadingToken}
          disabled={isTokenVisible}
          icon={
            <FontAwesomeIcon icon={isTokenVisible ? faCheckCircle : faKey} />
          }
        >
          {isTokenVisible ? "API token created" : "Create new API token"}
        </Button>
      }
      bottomDescription={
        isTokenVisible && !isLoadingToken ? (
          <TokenCopyBlock tokenResponse={tokenResponse} />
        ) : undefined
      }
    />
  );
};
